import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resetPassword as firebaseResetPassword } from '../lib/firebase';
import { useTeam } from '../contexts/TeamContext';
import Layout from '../components/Layout';
import { API_URL, getAuthHeaders } from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import { SUPPORTED_PLATFORMS, platformLabel, platformIcon, platformColor } from '../utils/platform';

interface ProfileData {
	user_id: string;
	email: string;
	display_name: string | null;
	photo_url: string | null;
	has_password: boolean;
	subscription_tier: string;
	created_at: number;
	subscription: {
		tier: string;
		status: string;
		current_period_end: number;
	} | null;
	mfa_enabled: boolean;
}

type Tab = 'profile' | 'password' | 'security' | 'linked-accounts' | 'billing' | 'danger';

type MFAState = 'disabled' | 'setup' | 'enabled';

export default function ProfilePage() {
	const { firebaseUser, logout } = useAuth();
	const { currentTeam } = useTeam();
	const navigate = useNavigate();
	const isMember = currentTeam?.role === 'member';
	const [activeTab, setActiveTab] = useState<Tab>('profile');
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [profile, setProfile] = useState<ProfileData | null>(null);

	// Profile form
	const [displayName, setDisplayName] = useState('');

	// Password form
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');

	// Delete account
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
	const [deleting, setDeleting] = useState(false);

	// MFA state
	const [mfaState, setMfaState] = useState<MFAState>('disabled');
	const [mfaSecret, setMfaSecret] = useState('');
	const [mfaOtpAuthUri, setMfaOtpAuthUri] = useState('');
	const [mfaCode, setMfaCode] = useState('');
	const [mfaSetupLoading, setMfaSetupLoading] = useState(false);
	const [liveCode, setLiveCode] = useState('');
	const [timeRemaining, setTimeRemaining] = useState(30);
	const [showDisableModal, setShowDisableModal] = useState(false);
	const [disableCode, setDisableCode] = useState('');
	const [disabling, setDisabling] = useState(false);
	const [secretCopied, setSecretCopied] = useState(false);
	const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Billing state
	const [subscription, setSubscription] = useState<{
		has_subscription: boolean;
		customer_id: string | null;
		subscription_id: string | null;
		status: string;
		code?: string;
	} | null>(null);
	const [openingPortal, setOpeningPortal] = useState(false);
	const [stripeMismatch, setStripeMismatch] = useState(false);
	const [resettingAccount, setResettingAccount] = useState(false);
	const [workspaceCount, setWorkspaceCount] = useState(1);
	const [seatCount, setSeatCount] = useState(1);
	const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
	const [creatingCheckout, setCreatingCheckout] = useState(false);

	// Minimum values based on current usage
	const minWorkspaces = Math.max(1, currentTeam?.license?.current_agents || 0);
	const minSeats = Math.max(1, currentTeam?.license?.current_seats || 0);

	// Linked accounts state
	const [identities, setIdentities] = useState<any[]>([]);
	const [identitiesLoading, setIdentitiesLoading] = useState(false);
	const [linkingPlatform, setLinkingPlatform] = useState<string | null>(null);
	const [linkCode, setLinkCode] = useState<string | null>(null);
	const [linkExpiry, setLinkExpiry] = useState<number | null>(null);
	const [linkSuccess, setLinkSuccess] = useState<string | null>(null);
	const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
	const [workspaces, setWorkspaces] = useState<any[]>([]);


	useEffect(() => {
		const fetchProfile = async () => {
			if (!firebaseUser) return;

			try {
				const idToken = await firebaseUser.getIdToken();
				const response = await fetch(`${API_URL}/api/v1/profile`, {
					headers: getAuthHeaders(idToken),
				});

				if (response.ok) {
					const data: ProfileData = await response.json();
					setProfile(data);
					setDisplayName(data.display_name || '');
					setMfaState(data.mfa_enabled ? 'enabled' : 'disabled');
				} else {
					setError('Failed to load profile');
				}
			} catch (err) {
				console.error('Error fetching profile:', err);
				setError('Error loading profile');
			} finally {
				setLoading(false);
			}
		};

		fetchProfile();
	}, [firebaseUser]);

	// Fetch subscription status
	useEffect(() => {
		const fetchSubscription = async () => {
			if (!firebaseUser) return;
			try {
				const idToken = await firebaseUser.getIdToken();
				const response = await fetch(`${API_URL}/api/v1/billing/subscription`, {
					headers: getAuthHeaders(idToken),
				});
				if (response.ok) {
					const data = await response.json();
					setSubscription(data);
					if (data.code === 'stripe_mode_mismatch') {
						setStripeMismatch(true);
					}
				}
			} catch (err) {
				console.error('Error fetching subscription:', err);
			}
		};

		if (activeTab === 'billing') {
			fetchSubscription();
		}
	}, [firebaseUser, activeTab]);

	// Fetch live TOTP code from server
	const fetchLiveCode = useCallback(async () => {
		if (!firebaseUser || mfaState !== 'enabled') return;
		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/mfa/code`, {
				headers: getAuthHeaders(idToken),
			});
			if (response.ok) {
				const data = await response.json();
				setLiveCode(data.code);
				setTimeRemaining(data.time_remaining);
			}
		} catch (err) {
			console.error('Error fetching TOTP code:', err);
		}
	}, [firebaseUser, mfaState]);

	// Fetch code on mount when MFA is enabled and security tab is active
	useEffect(() => {
		if (activeTab === 'security' && mfaState === 'enabled') {
			fetchLiveCode();
		}
		return () => {
			if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
		};
	}, [activeTab, mfaState, fetchLiveCode]);

	// Countdown timer — re-fetch from server when it reaches 0
	useEffect(() => {
		if (activeTab !== 'security' || mfaState !== 'enabled') return;

		countdownRef.current = setInterval(() => {
			setTimeRemaining((prev) => {
				if (prev <= 1) {
					// Re-fetch from server on period boundary
					fetchLiveCode();
					return 30;
				}
				return prev - 1;
			});
		}, 1000);

		return () => {
			if (countdownRef.current) clearInterval(countdownRef.current);
		};
	}, [activeTab, mfaState, fetchLiveCode]);

	const handleSaveProfile = async () => {
		if (!firebaseUser) return;

		setSaving(true);
		setError(null);
		setSuccess(null);

		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/profile`, {
				method: 'PUT',
				headers: getAuthHeaders(idToken),
				body: JSON.stringify({ display_name: displayName }),
			});

			if (response.ok) {
				setSuccess('Profile updated successfully');
				setProfile((prev) => prev ? { ...prev, display_name: displayName } : prev);
			} else {
				const data = await response.json().catch(() => ({ error: 'Failed to update profile' }));
				setError(data.error || 'Failed to update profile');
			}
		} catch (err) {
			console.error('Error updating profile:', err);
			setError('Error updating profile');
		} finally {
			setSaving(false);
		}
	};

	const handleChangePassword = async () => {
		if (!firebaseUser) return;

		if (newPassword !== confirmPassword) {
			setError('New passwords do not match');
			return;
		}

		if (newPassword.length < 8) {
			setError('New password must be at least 8 characters');
			return;
		}

		setSaving(true);
		setError(null);
		setSuccess(null);

		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/profile/password`, {
				method: 'PUT',
				headers: getAuthHeaders(idToken),
				body: JSON.stringify({
					current_password: currentPassword,
					new_password: newPassword,
				}),
			});

			if (response.ok) {
				setSuccess('Password updated successfully');
				setCurrentPassword('');
				setNewPassword('');
				setConfirmPassword('');
			} else {
				const data = await response.json().catch(() => ({ error: 'Failed to change password' }));
				setError(data.error || 'Failed to change password');
			}
		} catch (err) {
			console.error('Error changing password:', err);
			setError('Error changing password');
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteAccount = async () => {
		if (!firebaseUser || !profile) return;

		if (deleteConfirmEmail !== profile.email) {
			setError('Email does not match');
			return;
		}

		setDeleting(true);
		setError(null);

		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/profile`, {
				method: 'DELETE',
				headers: getAuthHeaders(idToken),
			});

			if (response.ok) {
				await logout();
				navigate('/login');
			} else {
				const data = await response.json().catch(() => ({ error: 'Failed to delete account' }));
				setError(data.error || 'Failed to delete account');
			}
		} catch (err) {
			console.error('Error deleting account:', err);
			setError('Error deleting account');
		} finally {
			setDeleting(false);
		}
	};

	// MFA handlers
	const handleEnableMFA = async () => {
		if (!firebaseUser) return;
		setMfaSetupLoading(true);
		setError(null);

		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/mfa/setup`, {
				method: 'POST',
				headers: getAuthHeaders(idToken),
			});

			if (response.ok) {
				const data = await response.json();
				setMfaSecret(data.secret);
				setMfaOtpAuthUri(data.otpauth_uri);
				setMfaState('setup');
				setMfaCode('');
			} else {
				const data = await response.json().catch(() => ({ error: 'Failed to start MFA setup' }));
				setError(data.error || 'Failed to start MFA setup');
			}
		} catch (err) {
			console.error('Error setting up MFA:', err);
			setError('Error setting up MFA');
		} finally {
			setMfaSetupLoading(false);
		}
	};

	const handleVerifySetup = async () => {
		if (!firebaseUser) return;
		const trimmed = mfaCode.replace(/\s/g, '');
		if (trimmed.length !== 6) {
			setError('Please enter a 6-digit code');
			return;
		}

		setMfaSetupLoading(true);
		setError(null);

		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/mfa/verify-setup`, {
				method: 'POST',
				headers: getAuthHeaders(idToken),
				body: JSON.stringify({ code: trimmed }),
			});

			if (response.ok) {
				setMfaState('enabled');
				setProfile((prev) => prev ? { ...prev, mfa_enabled: true } : prev);
				setSuccess('Privileged access authorization enabled');
				setMfaSecret('');
				setMfaOtpAuthUri('');
				setMfaCode('');
				// Fetch live code
				fetchLiveCode();
			} else {
				const data = await response.json().catch(() => ({ error: 'Invalid code' }));
				setError(data.error || 'Invalid code');
			}
		} catch (err) {
			console.error('Error verifying MFA setup:', err);
			setError('Error verifying setup');
		} finally {
			setMfaSetupLoading(false);
		}
	};

	const handleDisableMFA = async () => {
		if (!firebaseUser) return;
		const trimmed = disableCode.replace(/\s/g, '');
		if (trimmed.length !== 6) {
			setError('Please enter a 6-digit code');
			return;
		}

		setDisabling(true);
		setError(null);

		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/mfa`, {
				method: 'DELETE',
				headers: getAuthHeaders(idToken),
				body: JSON.stringify({ code: trimmed }),
			});

			if (response.ok) {
				setMfaState('disabled');
				setProfile((prev) => prev ? { ...prev, mfa_enabled: false } : prev);
				setSuccess('Privileged access authorization disabled');
				setShowDisableModal(false);
				setDisableCode('');
				setLiveCode('');
			} else {
				const data = await response.json().catch(() => ({ error: 'Invalid code' }));
				setError(data.error || 'Invalid code');
			}
		} catch (err) {
			console.error('Error disabling MFA:', err);
			setError('Error disabling MFA');
		} finally {
			setDisabling(false);
		}
	};

	// Linked accounts handlers
	const fetchIdentities = useCallback(async () => {
		if (!firebaseUser) return [];
		try {
			const token = await firebaseUser.getIdToken();
			const res = await fetch(`${API_URL}/api/v1/identity/linked`, {
				headers: getAuthHeaders(token),
			});
			if (!res.ok) throw new Error('Failed to fetch');
			const data = await res.json();
			return data.identities || [];
		} catch (err) {
			console.error('Error fetching identities:', err);
			return [];
		}
	}, [firebaseUser]);

	const fetchWorkspaces = useCallback(async () => {
		if (!firebaseUser) return [];
		try {
			const token = await firebaseUser.getIdToken();
			const res = await fetch(`${API_URL}/api/v1/instances`, {
				headers: getAuthHeaders(token),
			});
			if (!res.ok) return [];
			const data = await res.json();
			return data.instances || [];
		} catch {
			return [];
		}
	}, [firebaseUser]);

	// Fetch identities and workspaces when linked-accounts tab is active
	useEffect(() => {
		if (activeTab !== 'linked-accounts') return;
		setIdentitiesLoading(true);
		Promise.all([fetchIdentities(), fetchWorkspaces()]).then(([ids, ws]) => {
			setIdentities(ids);
			setWorkspaces(ws);
			setIdentitiesLoading(false);
		});
	}, [activeTab, fetchIdentities, fetchWorkspaces]);

	// Poll for verification when a link code is active
	useEffect(() => {
		if (!linkCode || !linkingPlatform) return;
		const interval = setInterval(async () => {
			const updated = await fetchIdentities();
			setIdentities(updated);
			const linked = updated.find(
				(i: any) => i.platform === linkingPlatform && i.verified
			);
			if (linked) {
				setLinkCode(null);
				setLinkingPlatform(null);
				setLinkExpiry(null);
				setLinkSuccess(`Successfully linked your ${platformLabel(linkingPlatform)} account!`);
				setTimeout(() => setLinkSuccess(null), 5000);
			}
		}, 5000);
		return () => clearInterval(interval);
	}, [linkCode, linkingPlatform, fetchIdentities]);

	const handleRequestLinkCode = async (platform: string) => {
		if (!firebaseUser) return;
		setLinkingPlatform(platform);
		setLinkCode(null);
		setLinkExpiry(null);
		setLinkSuccess(null);
		setError(null);

		try {
			const token = await firebaseUser.getIdToken();
			const res = await fetch(`${API_URL}/api/v1/identity/link-request`, {
				method: 'POST',
				headers: { ...getAuthHeaders(token), 'Content-Type': 'application/json' },
				body: JSON.stringify({ platform }),
			});
			if (!res.ok) {
				const data = await res.json().catch(() => ({ error: 'Failed to generate link code' }));
				setError(data.error || 'Failed to generate link code');
				setLinkingPlatform(null);
				return;
			}
			const data = await res.json();
			setLinkCode(data.code);
			setLinkExpiry(data.expires_at);
		} catch (err) {
			console.error('Error requesting link code:', err);
			setError('Failed to generate link code');
			setLinkingPlatform(null);
		}
	};

	const handleCancelLink = () => {
		setLinkCode(null);
		setLinkingPlatform(null);
		setLinkExpiry(null);
	};

	const handleUnlinkIdentity = async (identityId: string) => {
		if (!firebaseUser) return;
		setUnlinkingId(identityId);
		setError(null);

		try {
			const token = await firebaseUser.getIdToken();
			const res = await fetch(`${API_URL}/api/v1/identity/${identityId}`, {
				method: 'DELETE',
				headers: getAuthHeaders(token),
			});
			if (!res.ok) {
				const data = await res.json().catch(() => ({ error: 'Failed to unlink' }));
				setError(data.error || 'Failed to unlink identity');
				return;
			}
			const updated = await fetchIdentities();
			setIdentities(updated);
			setSuccess('Identity unlinked successfully');
		} catch (err) {
			console.error('Error unlinking identity:', err);
			setError('Failed to unlink identity');
		} finally {
			setUnlinkingId(null);
		}
	};


	const handleOpenCustomerPortal = async () => {
		if (!firebaseUser) return;
		setOpeningPortal(true);
		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/billing/portal`, {
				headers: getAuthHeaders(idToken),
			});

			if (response.ok) {
				const { url } = await response.json();
				window.open(url, '_blank');
			} else {
				const data = await response.json();
				if (data.code === 'stripe_mode_mismatch') {
					setStripeMismatch(true);
				} else {
					setError(data.error || 'Failed to open billing portal');
				}
			}
		} catch (err) {
			console.error('Error opening portal:', err);
			setError('Failed to open billing portal');
		} finally {
			setOpeningPortal(false);
		}
	};

	const handleResetAccount = async () => {
		if (!firebaseUser) return;
		if (!confirm('This will remove your current subscription data and restore a 10-hour trial. Continue?')) return;

		setResettingAccount(true);
		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/billing/reset-subscription`, {
				method: 'POST',
				headers: getAuthHeaders(idToken),
			});

			if (response.ok) {
				setStripeMismatch(false);
				// Re-fetch subscription status
				const subRes = await fetch(`${API_URL}/api/v1/billing/subscription`, {
					headers: getAuthHeaders(idToken),
				});
				if (subRes.ok) {
					setSubscription(await subRes.json());
				}
			} else {
				const data = await response.json();
				setError(data.error || 'Failed to reset account');
			}
		} catch (err) {
			console.error('Error resetting account:', err);
			setError('Failed to reset account');
		} finally {
			setResettingAccount(false);
		}
	};

	const handleSubscribe = async () => {
		if (!firebaseUser) return;
		setCreatingCheckout(true);
		try {
			const idToken = await firebaseUser.getIdToken();
			const response = await fetch(`${API_URL}/api/v1/billing/checkout`, {
				method: 'POST',
				headers: getAuthHeaders(idToken),
				body: JSON.stringify({
					workspaceCount,
					seatCount,
					interval: billingInterval,
					email: firebaseUser.email,
				}),
			});

			if (response.ok) {
				const { url } = await response.json();
				window.location.href = url; // Redirect to Stripe Checkout
			} else {
				const data = await response.json();
				setError(data.error || 'Failed to create checkout session');
			}
		} catch (err) {
			console.error('Error creating checkout:', err);
			setError('Failed to create checkout session');
		} finally {
			setCreatingCheckout(false);
		}
	};

	const calculateTotal = () => {
		// Instance pricing (graduated)
		let workspaceCost = 0;
		if (workspaceCount <= 10) {
			workspaceCost = workspaceCount * 1.50;
		} else {
			workspaceCost = (10 * 1.50) + ((workspaceCount - 10) * 1.00);
		}

		// Seat pricing (flat)
		const seatCost = seatCount * 1.99;
		const monthlyTotal = workspaceCost + seatCost;

		if (billingInterval === 'year') {
			const yearlyTotal = monthlyTotal * 12 * 0.8; // 20% discount
			return {
				workspaceCost,
				seatCost,
				total: yearlyTotal,
				monthly: yearlyTotal / 12,
				savings: monthlyTotal * 12 - yearlyTotal,
			};
		}

		return {
			workspaceCost,
			seatCost,
			total: monthlyTotal,
			monthly: monthlyTotal,
			savings: 0,
		};
	};

	const handleCopySecret = async () => {
		try {
			await navigator.clipboard.writeText(mfaSecret);
			setSecretCopied(true);
			setTimeout(() => setSecretCopied(false), 2000);
		} catch {
			// Fallback
		}
	};

	const tierBadgeClass = (tier: string) => {
		switch (tier) {
			case 'pro':
				return 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300';
			case 'enterprise':
				return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
			default:
				return 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300';
		}
	};

	// Format TOTP code as "123 456"
	const formatCode = (code: string) => {
		if (code.length !== 6) return code;
		return `${code.slice(0, 3)} ${code.slice(3)}`;
	};

	const tabs = [
		{
			id: 'profile' as const,
			name: 'Profile Info',
			icon: (
				<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
				</svg>
			),
		},
		{
			id: 'password' as const,
			name: profile?.has_password ? 'Change Password' : 'Set Password',
			icon: (
				<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
				</svg>
			),
		},
		{
			id: 'security' as const,
			name: 'Security',
			icon: (
				<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
				</svg>
			),
		},
		{
			id: 'linked-accounts' as const,
			name: 'Linked Accounts',
			icon: (
				<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
				</svg>
			),
		},
		...(!isMember
			? [
					{
						id: 'billing' as const,
						name: 'Billing',
						icon: (
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
							</svg>
						),
					},
				]
			: []),
		{
			id: 'danger' as const,
			name: 'Danger Zone',
			icon: (
				<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
				</svg>
			),
		},
	];

	if (loading) {
		return (
			<Layout title="Profile" subtitle="Manage your account">
				<div className="flex items-center justify-center h-64">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout title="Profile" subtitle="Manage your account">
			<div className="space-y-6">
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
					{/* Tabs */}
					<div className="border-b border-slate-200 dark:border-slate-800">
						<div className="flex space-x-1 p-2">
							{tabs.map((tab) => (
								<button
									key={tab.id}
									onClick={() => {
										setActiveTab(tab.id);
										setError(null);
										setSuccess(null);
									}}
									className={`flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${
										activeTab === tab.id
											? tab.id === 'danger'
												? 'bg-red-600 text-white shadow-lg'
												: 'bg-gradient-to-r from-primary-500 to-secondary-600 text-white shadow-lg'
											: tab.id === 'danger'
												? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
												: 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
									}`}
								>
									<div className={
										activeTab === tab.id
											? 'text-white'
											: tab.id === 'danger'
												? 'text-red-500 dark:text-red-400'
												: 'text-slate-600 dark:text-slate-400'
									}>
										{tab.icon}
									</div>
									<span>{tab.name}</span>
								</button>
							))}
						</div>
					</div>

					<div className="p-6">
						{/* Error Message */}
						{error && (
							<div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
								<div className="flex items-start space-x-3">
									<svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
									</svg>
									<p className="text-sm text-red-800 dark:text-red-400">{error}</p>
								</div>
							</div>
						)}

						{/* Success Message */}
						{success && (
							<div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
								<div className="flex items-start space-x-3">
									<svg className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
									</svg>
									<p className="text-sm text-green-800 dark:text-green-400">{success}</p>
								</div>
							</div>
						)}

						{/* Profile Info Tab */}
						{activeTab === 'profile' && profile && (
							<div className="space-y-6">
								{/* Avatar and basic info */}
								<div className="flex items-center space-x-4">
									{profile.photo_url ? (
										<img
											src={profile.photo_url}
											alt={profile.display_name || profile.email}
											className="w-16 h-16 rounded-full shadow-lg object-cover"
											referrerPolicy="no-referrer"
										/>
									) : (
										<div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-full flex items-center justify-center text-white text-2xl font-semibold shadow-lg">
											{(profile.display_name || profile.email).charAt(0).toUpperCase()}
										</div>
									)}
									<div>
										<h3 className="text-lg font-semibold text-slate-900 dark:text-white">
											{profile.display_name || profile.email}
										</h3>
										<p className="text-sm text-slate-500 dark:text-slate-400">{profile.email}</p>
									</div>
								</div>

								{/* Display Name */}
								<div>
									<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
										Display Name
									</label>
									<input
										type="text"
										value={displayName}
										onChange={(e) => setDisplayName(e.target.value)}
										placeholder="Enter your display name"
										maxLength={100}
										className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
									/>
									<p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
										{displayName.length}/100 characters
									</p>
								</div>

								{/* Email (read-only) */}
								<div>
									<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
										Email
									</label>
									<input
										type="email"
										value={profile.email}
										disabled
										className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 cursor-not-allowed"
									/>
									<p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
										Email is managed by your authentication provider
									</p>
								</div>

								{/* Subscription Tier */}
								<div>
									<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
										Subscription
									</label>
									<div className="flex items-center space-x-3">
										<span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize ${tierBadgeClass(profile.subscription_tier)}`}>
											{profile.subscription_tier}
										</span>
										{profile.subscription && (
											<span className="text-xs text-slate-500 dark:text-slate-400">
												{profile.subscription.status === 'active' ? 'Active' : profile.subscription.status}
												{profile.subscription.current_period_end && (
													<> until {new Date(profile.subscription.current_period_end).toLocaleDateString()}</>
												)}
											</span>
										)}
									</div>
								</div>

								{/* Member Since */}
								<div>
									<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
										Member Since
									</label>
									<p className="text-sm text-slate-900 dark:text-white">
										{new Date(profile.created_at).toLocaleDateString('en-US', {
											year: 'numeric',
											month: 'long',
											day: 'numeric',
										})}
									</p>
								</div>

								{/* Save Button */}
								<div className="flex justify-end">
									<button
										onClick={handleSaveProfile}
										disabled={saving}
										className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed"
									>
										{saving ? 'Saving...' : 'Save Changes'}
									</button>
								</div>
							</div>
						)}

						{/* Password Tab */}
						{activeTab === 'password' && (
							<div className="space-y-6">
								{!profile?.has_password ? (
									/* Social-login users: offer to set a password via email */
									<>
										<div>
											<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Set a Password</h3>
											<p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
												You signed up with a social login provider. You can set a password to also sign in with email and password.
											</p>
										</div>
										<div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6">
											<p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
												We'll send a password setup link to <strong className="text-slate-900 dark:text-white">{profile?.email}</strong>. Click the link in the email to create your password.
											</p>
											<button
												onClick={async () => {
													if (!profile?.email) return;
													setSaving(true);
													setError(null);
													setSuccess(null);
													try {
														await firebaseResetPassword(profile.email);
														setSuccess('Password setup email sent. Check your inbox.');
													} catch (err: any) {
														setError(err.message || 'Failed to send password setup email');
													} finally {
														setSaving(false);
													}
												}}
												disabled={saving}
												className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed"
											>
												{saving ? 'Sending...' : 'Send Password Setup Email'}
											</button>
										</div>
									</>
								) : (
									/* Email/password users: change password form */
									<>
								<div>
									<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Change Password</h3>
									<p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
										Update your password to keep your account secure
									</p>
								</div>

								<div>
									<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
										Current Password
									</label>
									<input
										type="password"
										value={currentPassword}
										onChange={(e) => setCurrentPassword(e.target.value)}
										placeholder="Enter current password"
										className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
										New Password
									</label>
									<input
										type="password"
										value={newPassword}
										onChange={(e) => setNewPassword(e.target.value)}
										placeholder="Enter new password (min 8 characters)"
										className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
										Confirm New Password
									</label>
									<input
										type="password"
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										placeholder="Confirm new password"
										className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
									/>
									{confirmPassword && newPassword !== confirmPassword && (
										<p className="text-xs text-red-500 mt-1">Passwords do not match</p>
									)}
								</div>

								<div className="flex justify-end">
									<button
										onClick={handleChangePassword}
										disabled={saving || !currentPassword || !newPassword || newPassword !== confirmPassword}
										className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed"
									>
										{saving ? 'Updating...' : 'Update Password'}
									</button>
								</div>
									</>
								)}
							</div>
						)}

						{/* Security Tab */}
						{activeTab === 'security' && (
							<div className="space-y-6">
								<div>
									<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Privileged Access Authorization</h3>
									<p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
										Enable TOTP verification for authorizing privileged operations on your AI agents
									</p>
								</div>

								{/* State A: MFA Not Enabled */}
								{mfaState === 'disabled' && (
									<div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6">
										<div className="flex items-start space-x-4">
											<div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
												<svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
												</svg>
											</div>
											<div className="flex-1">
												<div className="space-y-3 mb-4">
													<p className="text-sm text-slate-600 dark:text-slate-400">
														<strong className="text-slate-900 dark:text-white">What is PAM?</strong> Privileged Access Management adds an extra layer of security for sensitive operations on your AI agents.
													</p>
													<p className="text-sm text-slate-600 dark:text-slate-400">
														<strong className="text-slate-900 dark:text-white">What it protects:</strong> Process control (start/stop agents), threat response actions, security policy changes, and credential management.
													</p>
													<p className="text-sm text-slate-600 dark:text-slate-400">
														<strong className="text-slate-900 dark:text-white">How it works:</strong> Uses time-based one-time passwords (TOTP) from your authenticator app &mdash; the same standard used by Google, GitHub, and AWS.
													</p>
													<p className="text-sm text-slate-600 dark:text-slate-400">
														<strong className="text-slate-900 dark:text-white">Why enable it:</strong> Prevents unauthorized privileged actions even if your account is compromised.
													</p>
												</div>
												<button
													onClick={handleEnableMFA}
													disabled={mfaSetupLoading}
													className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed"
												>
													{mfaSetupLoading ? 'Setting up...' : 'Enable Privileged Access'}
												</button>
											</div>
										</div>
									</div>
								)}

								{/* State B: Setup Flow */}
								{mfaState === 'setup' && (
									<div className="space-y-6">
										<div className="border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 rounded-xl p-6">
											<h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
												Step 1: Scan QR Code
											</h4>
											<p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
												Open your authenticator app (Google Authenticator, Authy, 1Password, etc.) and scan this QR code:
											</p>
											<div className="flex justify-center mb-4">
												<div className="bg-white p-4 rounded-xl">
													<QRCodeSVG value={mfaOtpAuthUri} size={200} />
												</div>
											</div>
											<p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
												Or enter this secret key manually:
											</p>
											<div className="flex items-center space-x-2">
												<code className="flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-mono text-slate-900 dark:text-white break-all">
													{mfaSecret}
												</code>
												<button
													onClick={handleCopySecret}
													className="px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
												>
													{secretCopied ? 'Copied!' : 'Copy'}
												</button>
											</div>
										</div>

										<div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6">
											<h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
												Step 2: Enter Verification Code
											</h4>
											<p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
												Enter the 6-digit code shown in your authenticator app to verify setup:
											</p>
											<div className="flex items-center space-x-3">
												<input
													type="text"
													inputMode="numeric"
													maxLength={6}
													value={mfaCode}
													onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, ''))}
													placeholder="000000"
													className="w-40 px-4 py-3 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-center text-xl font-mono tracking-widest placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
												/>
												<button
													onClick={handleVerifySetup}
													disabled={mfaSetupLoading || mfaCode.length !== 6}
													className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed"
												>
													{mfaSetupLoading ? 'Verifying...' : 'Verify & Enable'}
												</button>
											</div>
										</div>

										<button
											onClick={() => {
												setMfaState('disabled');
												setMfaSecret('');
												setMfaOtpAuthUri('');
												setMfaCode('');
											}}
											className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors cursor-pointer"
										>
											Cancel setup
										</button>
									</div>
								)}

								{/* State C: MFA Enabled — Live Code Display */}
								{mfaState === 'enabled' && (
									<div className="space-y-6">
										<div className="border border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10 rounded-xl p-6">
											<div className="flex items-center space-x-3 mb-6">
												<div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
													<svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
													</svg>
												</div>
												<span className="text-sm font-semibold text-green-700 dark:text-green-400">
													Privileged Access Enabled
												</span>
											</div>
											<p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
												This code authorizes privileged operations on your AI workspaces. You'll be prompted for it when performing sensitive actions like controlling agent processes or modifying security policies.
											</p>

											{/* Live TOTP Code */}
											{liveCode && (
												<div className="text-center mb-4">
													<p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
														Current Code
													</p>
													<div className="text-5xl font-mono font-bold text-slate-900 dark:text-white tracking-[0.3em] mb-4">
														{formatCode(liveCode)}
													</div>

													{/* Countdown */}
													<div className="flex items-center justify-center space-x-3">
														<div className="w-48 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
															<div
																className={`h-full rounded-full transition-all duration-1000 ease-linear ${
																	timeRemaining <= 5
																		? 'bg-red-500'
																		: timeRemaining <= 10
																			? 'bg-yellow-500'
																			: 'bg-green-500'
																}`}
																style={{ width: `${(timeRemaining / 30) * 100}%` }}
															/>
														</div>
														<span className={`text-sm font-mono font-medium ${
															timeRemaining <= 5
																? 'text-red-600 dark:text-red-400'
																: 'text-slate-600 dark:text-slate-400'
														}`}>
															{timeRemaining}s
														</span>
													</div>
												</div>
											)}
										</div>

										{/* Disable Button */}
										<div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6">
											<p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
												To disable privileged access authorization, you will need to enter a current code from your authenticator app.
											</p>
											<button
												onClick={() => setShowDisableModal(true)}
												className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer"
											>
												Disable Privileged Access
											</button>
										</div>
									</div>
								)}
							</div>
						)}

						{/* Linked Accounts Tab */}
						{activeTab === 'linked-accounts' && (
							<div className="space-y-6">
								<div>
									<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Linked Accounts</h3>
									<p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
										Link your messaging platform accounts to receive notifications and interact with your agents
									</p>
								</div>

								<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
									<div className="flex items-start space-x-3">
										<svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<div className="space-y-2">
											<p className="text-sm text-blue-800 dark:text-blue-200">
												<strong>Connect your identity:</strong> Linking connects your messaging identity so you can interact with AI workspaces directly from Telegram, Slack, or Discord.
											</p>
											<p className="text-sm text-blue-800 dark:text-blue-200">
												<strong>How it works:</strong> Generate a verification code here, send it to the bot on your platform, and your accounts are linked.
											</p>
											<p className="text-sm text-blue-800 dark:text-blue-200">
												<strong>After linking:</strong> Messages you send on the linked platform are routed through ClawdGate's security layer to your AI workspace.
											</p>
										</div>
									</div>
								</div>

								{/* Success message for linking */}
								{linkSuccess && (
									<div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
										<div className="flex items-start space-x-3">
											<svg className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
											<p className="text-sm text-green-800 dark:text-green-400">{linkSuccess}</p>
										</div>
									</div>
								)}

								{identitiesLoading ? (
									<div className="flex items-center justify-center py-12">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
									</div>
								) : (
									<>
										{/* Currently linked identities */}
										{identities.length > 0 && (
											<div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
												<div className="bg-slate-50 dark:bg-slate-800/50 px-6 py-3 border-b border-slate-200 dark:border-slate-700">
													<h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Linked Platforms</h4>
												</div>
												<div className="divide-y divide-slate-200 dark:divide-slate-700">
													{identities.map((identity: any) => {
														const meta = typeof identity.platform_meta === 'string'
															? JSON.parse(identity.platform_meta || '{}')
															: (identity.platform_meta || {});
														const displayName = meta.display_name || meta.username || identity.platform_user_id;
														return (
															<div key={identity.identity_id} className="px-6 py-4 flex items-center justify-between">
																<div className="flex items-center space-x-4">
																	<div className={`w-10 h-10 ${platformColor(identity.platform)} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
																		{platformIcon(identity.platform)}
																	</div>
																	<div>
																		<div className="flex items-center space-x-2">
																			<span className="text-sm font-medium text-slate-900 dark:text-white">
																				{platformLabel(identity.platform)}
																			</span>
																			{identity.verified ? (
																				<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
																					Verified
																				</span>
																			) : (
																				<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
																					Pending
																				</span>
																			)}
																		</div>
																		<p className="text-sm text-slate-500 dark:text-slate-400">
																			{displayName}
																		</p>
																		{identity.linked_at && (
																			<p className="text-xs text-slate-400 dark:text-slate-500">
																				Linked {new Date(identity.linked_at).toLocaleDateString('en-US', {
																					year: 'numeric',
																					month: 'short',
																					day: 'numeric',
																				})}
																			</p>
																		)}
																		{(() => {
																			const matched = workspaces.filter((ws: any) => (ws.channel_types || []).includes(identity.platform));
																			if (matched.length === 0) return (
																				<p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
																					No workspaces configured for {platformLabel(identity.platform)}
																				</p>
																			);
																			return (
																				<div className="flex items-center gap-1.5 mt-1 flex-wrap">
																					<span className="text-xs text-slate-400 dark:text-slate-500">Active on {matched.length} workspace{matched.length !== 1 ? 's' : ''}:</span>
																					{matched.map((ws: any) => (
																						<span
																							key={ws.instance_id}
																							className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
																						>
																							{ws.instance_name || ws.instance_id}
																						</span>
																					))}
																				</div>
																			);
																		})()}
																	</div>
																</div>
																<button
																	onClick={() => handleUnlinkIdentity(identity.identity_id)}
																	disabled={unlinkingId === identity.identity_id}
																	className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
																>
																	{unlinkingId === identity.identity_id ? 'Unlinking...' : 'Unlink'}
																</button>
															</div>
														);
													})}
												</div>
											</div>
										)}
										{/* Active link code display */}
										{linkCode && linkingPlatform && (
											<div className="border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 rounded-xl p-6">
												<div className="flex items-start space-x-4">
													<div className={`w-10 h-10 ${platformColor(linkingPlatform)} rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
														{platformIcon(linkingPlatform)}
													</div>
													<div className="flex-1">
														<h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
															Link {platformLabel(linkingPlatform)}
														</h4>
														<p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
															Send this verification code to your bot on {platformLabel(linkingPlatform)}:
														</p>
														<div className="flex items-center space-x-3 mb-4">
															<code className="text-3xl font-mono font-bold text-slate-900 dark:text-white tracking-[0.2em] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-6 py-3">
																{linkCode}
															</code>
														</div>
														<p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
															{linkExpiry
																? `Expires at ${new Date(linkExpiry).toLocaleTimeString()}`
																: 'Expires in 5 minutes'}
														</p>
														<p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
															Waiting for verification...
														</p>
														<details className="mb-4" open>
															<summary className="text-xs font-medium text-primary-600 dark:text-primary-400 cursor-pointer hover:text-primary-700 dark:hover:text-primary-300">
																Setup Instructions
															</summary>
															<div className="mt-2 p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
																{linkingPlatform === 'telegram' && (
																	<ol className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-decimal list-inside">
																		<li>Open Telegram and search for your team's bot (the bot name is configured by your admin)</li>
																		<li>Start a conversation with the bot by pressing "Start" or sending <code className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">/start</code></li>
																		<li>Send the verification code to the bot: <code className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">!link {linkCode}</code></li>
																		<li>Once verified, you can send messages to your AI workspace directly through Telegram</li>
																	</ol>
																)}
																{linkingPlatform === 'slack' && (
																	<ol className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-decimal list-inside">
																		<li>Your admin has added the ClawdGate bot to your Slack workspace</li>
																		<li>Open a direct message with the ClawdGate bot in Slack</li>
																		<li>Send the verification code: <code className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">!link {linkCode}</code></li>
																		<li>Once verified, you can interact with your AI workspace from any allowed Slack channel</li>
																	</ol>
																)}
																{linkingPlatform === 'discord' && (
																	<ol className="text-xs text-slate-600 dark:text-slate-400 space-y-1.5 list-decimal list-inside">
																		<li>Your admin has added the ClawdGate bot to your Discord server</li>
																		<li>Send a direct message to the bot, or use an allowed channel</li>
																		<li>Send the verification code: <code className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">!link {linkCode}</code></li>
																		<li>Once verified, you can chat with your AI workspace through Discord</li>
																	</ol>
																)}
															</div>
														</details>
														<button
															onClick={handleCancelLink}
															className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors cursor-pointer"
														>
															Cancel
														</button>
													</div>
												</div>
											</div>
										)}

										{/* Link buttons — always show all platforms (users can link multiple accounts per platform) */}
										{(() => {
											return (
												<div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6">
													<h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">
														Link a Platform Account
													</h4>
													<p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
														To use a platform, your team admin must first configure the integration in Settings. Once configured, link your account below and use the verification code to connect.
													</p>
													<p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
														You can link multiple accounts per platform (e.g., two Telegram accounts).
													</p>
													<div className="flex flex-wrap gap-3">
														{SUPPORTED_PLATFORMS.map((platform) => (
															<button
																key={platform}
																onClick={() => handleRequestLinkCode(platform)}
																disabled={linkingPlatform === platform}
																className="inline-flex items-center space-x-2 px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
															>
																<div className={`w-6 h-6 ${platformColor(platform)} rounded-full flex items-center justify-center text-white text-xs font-bold`}>
																	{platformIcon(platform)}
																</div>
																<span>Link {platformLabel(platform)}</span>
															</button>
														))}
													</div>
												</div>
											);
										})()}

										{/* Empty state */}
										{identities.length === 0 && !linkCode && (
											<div className="text-center py-8">
												<div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
													<svg className="w-8 h-8 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
													</svg>
												</div>
												<p className="text-sm text-slate-500 dark:text-slate-400">
													No linked accounts yet. Link a messaging platform to get started.
												</p>
											</div>
										)}
									</>
								)}
							</div>
						)}

						{/* Billing Tab */}
						{activeTab === 'billing' && (
							<div className="space-y-6">
								{/* Stripe Mode Mismatch Warning */}
								{stripeMismatch && (
									<div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-6">
										<div className="flex items-start gap-3">
											<svg className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
											</svg>
											<div className="flex-1">
												<h3 className="text-lg font-semibold text-amber-800 dark:text-amber-300 mb-1">
													Stripe Mode Mismatch
												</h3>
												<p className="text-sm text-amber-700 dark:text-amber-400 mb-3">
													Your subscription was created in a different Stripe environment (test vs. live mode).
													This prevents billing operations from working. Reset your account to clear the stale
													data and restore a fresh 10-hour trial, then re-subscribe.
												</p>
												<button
													onClick={handleResetAccount}
													disabled={resettingAccount}
													className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-semibold rounded-lg transition-colors"
												>
													{resettingAccount ? 'Resetting...' : 'Reset Account'}
												</button>
											</div>
										</div>
									</div>
								)}

								{/* Subscription Status */}
								{subscription?.has_subscription ? (
									<div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6">
										<div className="flex items-center justify-between mb-4">
											<div>
												<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
													Current Subscription
												</h3>
												<p className="text-sm text-slate-600 dark:text-slate-400">
													Status:{' '}
													<span
														className={`font-medium ${
															subscription.status === 'active'
																? 'text-green-600 dark:text-green-400'
																: subscription.status === 'past_due'
																? 'text-yellow-600 dark:text-yellow-400'
																: subscription.status === 'canceled'
																? 'text-red-600 dark:text-red-400'
																: 'text-slate-600 dark:text-slate-400'
														}`}
													>
														{subscription.status === 'active' && '✓ Active'}
														{subscription.status === 'past_due' && '⚠ Past Due'}
														{subscription.status === 'canceled' && '✗ Canceled'}
														{!['active', 'past_due', 'canceled'].includes(subscription.status) &&
															subscription.status}
													</span>
												</p>
												{subscription.customer_id && (
													<p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
														Customer ID: {subscription.customer_id}
													</p>
												)}
											</div>
											<button
												onClick={handleOpenCustomerPortal}
												disabled={openingPortal}
												className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer"
											>
												{openingPortal ? 'Opening...' : 'Manage Subscription'}
											</button>
										</div>

										<div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
											<p className="text-sm text-blue-800 dark:text-blue-200">
												<strong>Manage your subscription:</strong> Click "Manage Subscription" to
												update quantities, change payment methods, view invoices, or cancel your
												subscription.
											</p>
										</div>
									</div>
								) : (
									<>
										{/* Subscription Builder */}
										<div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
											{/* Billing Interval Toggle */}
											<div className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 px-8 py-6">
												<div className="flex items-center justify-center gap-3">
													<button
														onClick={() => setBillingInterval('month')}
														className={`px-8 py-3 rounded-lg font-semibold transition-all ${
															billingInterval === 'month'
																? 'bg-primary-600 text-white shadow-md'
																: 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
														}`}
													>
														Monthly
													</button>
													<button
														onClick={() => setBillingInterval('year')}
														className={`px-8 py-3 rounded-lg font-semibold transition-all relative ${
															billingInterval === 'year'
																? 'bg-primary-600 text-white shadow-md'
																: 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
														}`}
													>
														Yearly
														<span className="ml-2 inline-block px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
															Save 20%
														</span>
													</button>
												</div>
											</div>

											{/* Configuration */}
											<div className="p-8 space-y-8">
												{/* Bots */}
												<div>
													<label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
														AI Workspaces
													</label>
													<input
														type="number"
														min={minWorkspaces}
														max="1000"
														value={workspaceCount}
														onChange={(e) => setWorkspaceCount(Math.max(minWorkspaces, parseInt(e.target.value) || minWorkspaces))}
														className="w-full px-4 py-3 text-lg border-2 border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:border-primary-500 dark:focus:border-primary-400 transition-colors"
													/>
													<p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
														Each workspace is one protected AI agent instance. $1.50/workspace (1-10) · $1/workspace (11+)
														{minWorkspaces > 1 && <span className="block text-amber-600 dark:text-amber-400 mt-0.5">Minimum {minWorkspaces} (currently in use)</span>}
													</p>
												</div>

												{/* Seats */}
												<div>
													<label className="block text-sm font-semibold text-slate-900 dark:text-white mb-3">
														Team Seats
													</label>
													<input
														type="number"
														min={minSeats}
														max="1000"
														value={seatCount}
														onChange={(e) => setSeatCount(Math.max(minSeats, parseInt(e.target.value) || minSeats))}
														className="w-full px-4 py-3 text-lg border-2 border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:border-primary-500 dark:focus:border-primary-400 transition-colors"
													/>
													<p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
														Each seat is one team member with console access. $1.99/seat
														{minSeats > 1 && <span className="block text-amber-600 dark:text-amber-400 mt-0.5">Minimum {minSeats} (currently in use)</span>}
													</p>
												</div>

												{/* Price Summary */}
												<div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6 space-y-3">
													{(() => {
														const price = calculateTotal();
														return (
															<>
																<div className="flex justify-between text-slate-700 dark:text-slate-300">
																	<span>{workspaceCount} Workspace{workspaceCount !== 1 ? 's' : ''}</span>
																	<span className="font-medium">${price.workspaceCost.toFixed(2)}/mo</span>
																</div>
																<div className="flex justify-between text-slate-700 dark:text-slate-300">
																	<span>{seatCount} Seat{seatCount !== 1 ? 's' : ''}</span>
																	<span className="font-medium">${price.seatCost.toFixed(2)}/mo</span>
																</div>

																<div className="border-t border-slate-300 dark:border-slate-600 pt-3 mt-3">
																	<div className="flex justify-between items-center">
																		<span className="text-lg font-semibold text-slate-900 dark:text-white">
																			{billingInterval === 'year' ? 'Total per year' : 'Total per month'}
																		</span>
																		<span className="text-2xl font-bold text-slate-900 dark:text-white">
																			${price.total.toFixed(2)}
																		</span>
																	</div>
																	{billingInterval === 'year' && (
																		<>
																			<div className="flex justify-between text-sm text-slate-600 dark:text-slate-400 mt-2">
																				<span>Effective monthly</span>
																				<span>${price.monthly.toFixed(2)}/month</span>
																			</div>
																			<div className="flex justify-between text-sm text-green-600 dark:text-green-400 font-medium mt-1">
																				<span>Annual savings</span>
																				<span>-${price.savings.toFixed(2)}</span>
																			</div>
																		</>
																	)}
																</div>
															</>
														);
													})()}
												</div>

												{/* Subscribe Button */}
												<button
													onClick={handleSubscribe}
													disabled={creatingCheckout}
													className="w-full py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed"
												>
													{creatingCheckout ? 'Loading...' : 'Subscribe Now'}
												</button>

												<p className="text-center text-sm text-slate-500 dark:text-slate-400">
													Secure payment powered by{' '}
													<a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline font-medium">
														Stripe
													</a>
												</p>
											</div>
										</div>

										{/* Features */}
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6">
												<h3 className="font-semibold text-slate-900 dark:text-white mb-2">🔒 Enterprise Security</h3>
												<p className="text-sm text-slate-600 dark:text-slate-400">
													Prompt injection protection, credential security, and policy enforcement
												</p>
											</div>
											<div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6">
												<h3 className="font-semibold text-slate-900 dark:text-white mb-2">📊 Complete Visibility</h3>
												<p className="text-sm text-slate-600 dark:text-slate-400">
													90-day event retention, audit logs, and real-time monitoring
												</p>
											</div>
										</div>
									</>
								)}

								{/* Pricing Information */}
								<div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6">
									<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Pricing</h3>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div className="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
											<h4 className="font-semibold text-slate-900 dark:text-white mb-2">AI Workspaces</h4>
											<p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Each workspace protects one AI agent instance on your device.</p>
											<ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
												<li>• $1.50/workspace/month for 1-10 workspaces</li>
												<li>• $1/workspace/month for 11+ workspaces</li>
												<li className="text-green-600 dark:text-green-400 font-medium">
													• ~20% discount on annual plans
												</li>
											</ul>
										</div>

										<div className="border border-slate-200 dark:border-slate-600 rounded-lg p-4">
											<h4 className="font-semibold text-slate-900 dark:text-white mb-2">Team Seats</h4>
											<p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Each seat grants one team member access to the ClawdGate console.</p>
											<ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
												<li>• $1.99/seat/month (flat rate)</li>
												<li className="text-green-600 dark:text-green-400 font-medium">
													• ~20% discount on annual plans
												</li>
											</ul>
										</div>
									</div>

									<div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800">
										<p className="text-sm text-green-800 dark:text-green-200">
											<strong>Entry plan:</strong> $5.99/month for 1 workspace + 1 seat
										</p>
									</div>
								</div>
							</div>
						)}

						{/* Danger Zone Tab */}
						{activeTab === 'danger' && (
							<div className="space-y-6">
								<div className="border border-red-200 dark:border-red-800 rounded-xl p-6">
									<h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">Delete Account</h3>
									<p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
										Permanently delete your account and all associated data. This includes all agents, bot instances, policies, credentials, and chat history. This action cannot be undone.
									</p>
									<button
										onClick={() => setShowDeleteModal(true)}
										className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer"
									>
										Delete Account
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Delete Confirmation Modal */}
			{showDeleteModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
					<div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6">
						<div className="flex items-center space-x-3 mb-4">
							<div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
								<svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
								</svg>
							</div>
							<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Delete Account</h3>
						</div>

						<p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
							This will permanently delete your account, all agents, instances, and data. This action cannot be undone.
						</p>

						<div className="mb-4">
							<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
								Type <span className="font-mono text-red-600 dark:text-red-400">{profile?.email}</span> to confirm
							</label>
							<input
								type="text"
								value={deleteConfirmEmail}
								onChange={(e) => setDeleteConfirmEmail(e.target.value)}
								placeholder="Enter your email"
								className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
							/>
						</div>

						{error && (
							<p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
						)}

						<div className="flex justify-end space-x-3">
							<button
								onClick={() => {
									setShowDeleteModal(false);
									setDeleteConfirmEmail('');
									setError(null);
								}}
								className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
							>
								Cancel
							</button>
							<button
								onClick={handleDeleteAccount}
								disabled={deleting || deleteConfirmEmail !== profile?.email}
								className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-slate-400 text-white rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed"
							>
								{deleting ? 'Deleting...' : 'Delete Account'}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Disable MFA Confirmation Modal */}
			{showDisableModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
					<div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6">
						<div className="flex items-center space-x-3 mb-4">
							<div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
								<svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
								</svg>
							</div>
							<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Disable Privileged Access</h3>
						</div>

						<p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
							Enter a code from your authenticator app to confirm disabling privileged access authorization.
						</p>

						<div className="mb-4">
							<input
								type="text"
								inputMode="numeric"
								maxLength={6}
								value={disableCode}
								onChange={(e) => setDisableCode(e.target.value.replace(/[^0-9]/g, ''))}
								placeholder="000000"
								className="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-center text-xl font-mono tracking-widest placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
							/>
						</div>

						<div className="flex justify-end space-x-3">
							<button
								onClick={() => {
									setShowDisableModal(false);
									setDisableCode('');
									setError(null);
								}}
								className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
							>
								Cancel
							</button>
							<button
								onClick={handleDisableMFA}
								disabled={disabling || disableCode.length !== 6}
								className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-slate-400 text-white rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed"
							>
								{disabling ? 'Disabling...' : 'Disable Privileged Access'}
							</button>
						</div>
					</div>
				</div>
			)}
		</Layout>
	);
}
